import React from 'react';
import AvrgirlArduino from 'avrgirl-arduino';
import { ESPLoader, Transport } from 'esptool-js';
import { string } from 'prop-types';
import apiClient from '../../utils/apiClient.js';
import { openUploadProgressModal } from './UploadProgressModal.jsx';
const Scratch3RoboticArmBlocks = require('../../../../stemblocks-VM/src/extensions/Robotic_Arm/index');
const Scratch3ArduinoUnoBlocks = require('../../../../stemblocks-VM/src/extensions/scratch3_arduino/index.js');
const Scratch3ArduinoRoboticCarBlocks = require('../../../../stemblocks-VM/src/extensions/scratch3_car/index.js');
const Scratch3RoboticSpiderBlocks = require('../../../../stemblocks-VM/src/extensions/Robotic_Spider/index.js');
const Scratch3SensorsBlocks = require('../../../../stemblocks-VM/src/extensions/sensors/index.js');
const Scratch3ActuatorsBlocks = require('../../../../stemblocks-VM/src/extensions/actuators/index.js');
const Scratch3DisplayBlocks = require('../../../../stemblocks-VM/src/extensions/display_modules/index.js');
const StemEngineBlocks = require('../../../../stemblocks-VM/src/extensions/stemengine/index.js');
const PythonImportsExtension = require('../../../../stemblocks-VM/src/extensions/python_blocks/imports.js');
const PythonRobo_car = require('../../../../stemblocks-VM/src/extensions/python_blocks/robotic_car.js');
const pythonRobo_arm = require('../../../../stemblocks-VM/src/extensions/python_blocks/robotic_arm.js');
const pythonRobo_spider = require('../../../../stemblocks-VM/src/extensions/python_blocks/robotic_spider.js');
const codeGeneration = require('../../../../stemblocks-VM/src/extensions/codeGeneration.js');
const Esp32Blocks = require('../../../../stemblocks-VM/src/extensions/esp32/index.js');
const Scratch3StemSensorsBlocks = require('../../../../stemblocks-VM/src/extensions/stemsensors/index.js');
const Scratch3StemActuatorsBlocks = require('../../../../stemblocks-VM/src/extensions/stemactuators/index.js');

const OPTIBOOT = {
    STK_OK: 0x10,
    STK_INSYNC: 0x14,
    STK_GET_SYNC: 0x30,
    STK_PROG_PAGE: 0x64,
    STK_LOAD_ADDRESS: 0x55,
    STK_READ_PAGE: 0x74,
    STK_CRC_EOP: 0x20
};

async function flashESP32(port, firmwareBuffer, existingModal = null) {
    let transport = null;
    let espLoader = null;
    const progressModal = existingModal || openUploadProgressModal();

    try {
        console.log('Starting ESP32 flash process...');
        progressModal.setProgress(45);
        progressModal.setStatus('Connecting to ESP32 / Stem Engine...');
        progressModal.appendLog('[ESP32] Initializing WebSerial transport...\n');

        // Convert buffer to required format
        const uint8Array = new Uint8Array(firmwareBuffer);
        const binaryString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');

        // Initialize transport and custom terminal for log redirection
        transport = new Transport(port);
        const customTerminal = {
            clean() {
                progressModal.clearLogs();
            },
            writeLine(data) {
                progressModal.appendLog(`${data}\n`);
                console.log('[ESP32]', data);
            },
            write(data) {
                progressModal.appendLog(data);
                console.log('[ESP32]', data);
            }
        };

        espLoader = new ESPLoader({
            transport,
            baudrate: 115200,
            romBaudrate: 115200,
            terminal: customTerminal,
            debugLogging: true
        });

        console.log('ESPLoader initialized');
        progressModal.appendLog('[ESP32] ESPLoader initialized successfully.\n');

        const isWindows = navigator.platform.indexOf('Win') > -1;
        if (isWindows) {
            console.log('Windows platform detected, applying specific reset sequence');
            progressModal.setStatus('Applying serial reset sequence...');
            progressModal.appendLog('[ESP32] Windows platform detected, toggling DTR/RTS...\n');
            try {
                // Implement Windows-specific reset sequence
                await transport.setDTR(false);
                await new Promise(resolve => setTimeout(resolve, 100));
                await transport.setRTS(true);
                await new Promise(resolve => setTimeout(resolve, 100));
                await transport.setDTR(true);
                await new Promise(resolve => setTimeout(resolve, 100));
                await transport.setRTS(false);
                await new Promise(resolve => setTimeout(resolve, 250));
            } catch (resetError) {
                console.warn('Windows-specific reset sequence failed:', resetError);
                progressModal.appendLog(`[ESP32] Warning: Reset sequence warning: ${resetError.message}\n`);
            }
        }

        // Let ESPLoader handle the connection
        let retries = 5;
        while (retries > 0) {
            try {
                progressModal.setProgress(50);
                progressModal.setStatus(`Detecting ESP32 chip (attempt ${6 - retries}/5)...`);
                await espLoader.detectChip();
                console.log('Connected to ESP32');
                progressModal.appendLog(`[ESP32] Chip detected: ${espLoader.chip?.CHIP_NAME || 'ESP32'}\n`);
                break;
            } catch (error) {
                retries--;
                console.warn(`Chip detection failed. Retries left: ${retries}`, error);
                progressModal.appendLog(`[ESP32] Chip detection attempt failed: ${error.message}. Retries remaining: ${retries}\n`);
                await new Promise(resolve => setTimeout(resolve, 1000));

                if (retries === 0) {
                    throw new Error('Failed to detect ESP32 chip after 5 attempts. Check physical USB connection and board power.');
                }
            }
        }

        progressModal.setProgress(60);
        console.log('Running stub...');
        progressModal.setStatus('Running bootloader stub...');
        progressModal.appendLog('[ESP32] Running bootloader stub...\n');
        await espLoader.runStub();

        progressModal.setProgress(65);
        console.log('Starting flash process...');
        progressModal.setStatus('Writing firmware to flash memory...');
        progressModal.appendLog('[ESP32] Erasing & flashing binary to address 0x0000...\n');

        await espLoader.writeFlash({
            fileArray: [
                {
                    data: binaryString,
                    address: 0x0000
                }
            ],
            flashSize: 'keep',
            flashMode: 'QIO',
            flashFreq: '40MHz',
            eraseAll: false,
            compress: true,
            reportProgress: (fileIndex, written, total) => {
                const percent = total ? Math.round((written / total) * 100) : 0;
                const scaled = Math.min(99, Math.round(65 + (percent * 0.34)));
                progressModal.setProgress(scaled);
                progressModal.setStatus(`Uploading firmware... ${percent}%`);
            }
        });

        console.log('Firmware flashing completed successfully');
        progressModal.setProgress(100);
        progressModal.setStatus('Firmware burned successfully!');
        progressModal.setMode('success');
        progressModal.appendLog('[ESP32] Flash completed successfully! Device is restarting.\n');

    } catch (error) {
        console.error('Complete flashing process failed:', error);
        progressModal.setMode('error');
        progressModal.setErrorMsg(error.message || 'Complete flashing process failed');
        progressModal.appendLog(`\n[ESP32 Flash Error]: ${error.message || error}\n`);
        throw error;
    } finally {
        try {
            // First do hard reset
            if (espLoader) {
                await espLoader.hardReset(true);
                console.log('Hard reset completed');
                progressModal.appendLog('[ESP32] Hard reset triggered.\n');
            }

            // Then disconnect transport
            if (transport) {
                await transport.disconnect();
                await transport.waitForUnlock(1500);
                console.log('Transport disconnected successfully');
                progressModal.appendLog('[ESP32] Transport disconnected.\n');
            }

            transport = null;
            espLoader = null;
        } catch (cleanupError) {
            console.warn('Error during cleanup:', cleanupError);
            progressModal.appendLog(`[ESP32] Cleanup info: ${cleanupError.message}\n`);
        }
    }
}

async function flashArduinoUno(firmwareBuffer, progressModal = null) {
    console.log('Flashing Arduino Uno...');
    if (progressModal) {
        progressModal.setProgress(60);
        progressModal.setStatus('Flashing Arduino Uno...');
        progressModal.appendLog('[Arduino Uno] Starting Avrgirl flasher...\n');
    }

    const avrgirl = new AvrgirlArduino({
        board: 'uno',
        debug: true
    });

    return new Promise((resolve, reject) => {
        avrgirl.flash(firmwareBuffer, error => {
            if (error) {
                console.error('Error flashing Arduino board:', error);
                if (progressModal) {
                    progressModal.setMode('error');
                    progressModal.setErrorMsg(error.message || 'Error flashing Arduino Uno');
                    progressModal.appendLog(`\n[Arduino Uno Error]: ${error.message}\n`);
                }
                reject(error);
            } else {
                console.log('Code successfully burned to the Arduino board');
                if (progressModal) {
                    progressModal.setProgress(100);
                    progressModal.setStatus('Code successfully burned to Arduino Uno!');
                    progressModal.setMode('success');
                    progressModal.appendLog('[Arduino Uno] Firmware upload successful!\n');
                }
                resolve();
            }
        });
    });
}

async function flashArduinoNano(firmwareBuffer, progressModal = null) {
    console.log('Flashing Arduino Nano...');
    if (progressModal) {
        progressModal.setProgress(60);
        progressModal.setStatus('Flashing Arduino Nano...');
        progressModal.appendLog('[Arduino Nano] Starting Avrgirl flasher...\n');
    }

    const avrgirl = new AvrgirlArduino({
        board: 'nano',
        debug: true
    });

    return new Promise((resolve, reject) => {
        avrgirl.flash(firmwareBuffer, error => {
            if (error) {
                console.error('Error flashing Arduino board:', error);
                if (progressModal) {
                    progressModal.setMode('error');
                    progressModal.setErrorMsg(error.message || 'Error flashing Arduino Nano');
                    progressModal.appendLog(`\n[Arduino Nano Error]: ${error.message}\n`);
                }
                reject(error);
            } else {
                console.log('Code successfully burned');
                if (progressModal) {
                    progressModal.setProgress(100);
                    progressModal.setStatus('Code successfully burned to Arduino Nano!');
                    progressModal.setMode('success');
                    progressModal.appendLog('[Arduino Nano] Firmware upload successful!\n');
                }
                resolve();
            }
        });
    });
}

// Add these helper functions
async function readWithTimeout(reader, timeout) {
    const result = await Promise.race([
        reader.read(),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Read timeout')), timeout)
        )
    ]);
    return result.value;
}

async function verifyResponse(reader) {
    const response = await readWithTimeout(reader, 1000);
    if (!response || response[0] !== OPTIBOOT.STK_INSYNC) {
        throw new Error('Invalid response from bootloader');
    }
    return response;
}

function resetAllBlocks(...blocks) {
    blocks.forEach(block => block.full_reset && block.full_reset());
    codeGeneration.resetCode();
    console.log('Code has been reset');
}

async function generateAndUploadCode(serialport) {
    // Retrieve selected board from localStorage
    let selectedBoardObject = localStorage.getItem('selectedBoard');
    if (!selectedBoardObject) {
        alert('Please select a board before proceeding.');
        return;
    }
    selectedBoardObject = JSON.parse(selectedBoardObject);
    const selectedBoard = selectedBoardObject.name || '';

    // Fixed board identification logic
    const selectedBoardLower = selectedBoard.toLowerCase();
    const isESP32 = selectedBoardLower === 'esp32';
    const isStemEngine = selectedBoardLower === 'stem engine';
    const isNano = (
        selectedBoardLower === 'arduino nano' ||
        selectedBoardLower === 'stem engine nano'
    );
    const baudRate = codeGeneration.getSerialBaudRate();

    // Fixed board configuration logic - prioritize Nano check first
    let boardConfig;
    if (isNano) {
        boardConfig = { board: 'arduino:avr:nano' };
    } else if (isESP32 || isStemEngine) {
        boardConfig = { board: 'esp32:esp32:esp32' };
    } else {
        boardConfig = { board: 'arduino:avr:uno' }; // Default to Uno
    }

    console.log('Selected Board:', selectedBoard);
    console.log('Board Config:', boardConfig);

    // Instantiate block classes
    const arduinoBlocks = new Scratch3ArduinoUnoBlocks();
    const armBlocks = new Scratch3RoboticArmBlocks();
    const carBlocks = new Scratch3ArduinoRoboticCarBlocks();
    const spiderBlocks = new Scratch3RoboticSpiderBlocks();
    const sensorBlock = new Scratch3SensorsBlocks();
    const actuatorBlock = new Scratch3ActuatorsBlocks();
    const displayBlock = new Scratch3DisplayBlocks();
    const stemEngine = new StemEngineBlocks();
    const pythonImports = new PythonImportsExtension();
    const pythonRoboCar = new PythonRobo_car();
    const pythonRoboArm = new pythonRobo_arm();
    const pythonRoboSpider = new pythonRobo_spider();
    const esp32Blocks = new Esp32Blocks();
    const stemSensorBlock = new Scratch3StemSensorsBlocks();
    const stemActuatorBlock = new Scratch3StemActuatorsBlocks();

    const finalCode = codeGeneration.generateFinalCode();
    console.log('Generated Code:', finalCode);

    // Open upload progress modal immediately
    const progressModal = openUploadProgressModal();
    progressModal.setProgress(10);
    progressModal.setStatus(`Preparing build for ${selectedBoard}...`);
    progressModal.appendLog(`[Build] Target Board: ${selectedBoard} (${boardConfig.board})\n`);
    progressModal.appendLog(`[Build] Sending code to cloud compiler service...\n`);

    try {
        progressModal.setProgress(20);
        progressModal.setStatus('Compiling firmware on cloud server...');

        // Request the server to compile and prepare the firmware file for download
        const response = await apiClient.post('/iot/burnCode', {
            code: finalCode,
            deviceType: 'arduino',
            board: boardConfig.board
        }, {
            responseType: 'blob' // Download file as blob
        });

        console.log('Firmware file received from server');
        progressModal.setProgress(40);
        progressModal.setStatus('Firmware compiled successfully! Preparing upload...');

        // Convert Blob to ArrayBuffer
        const firmwareBuffer = await response.data.arrayBuffer();
        progressModal.appendLog(`[Build] Firmware binary received (${firmwareBuffer.byteLength} bytes).\n`);

        if (selectedBoardLower === 'arduino uno') {
            await flashArduinoUno(firmwareBuffer, progressModal);
        } else if (isNano) {
            await flashArduinoNano(firmwareBuffer, progressModal);
        } else if (isESP32 || isStemEngine) {
            if (!serialport) {
                throw new Error(`Failed to open serial port for ${selectedBoard}. Please connect the board and select the port.`);
            }
            await flashESP32(serialport, firmwareBuffer, progressModal);
            return {
                baudRate: baudRate || 9600
            };
        } else {
            throw new Error(`Unsupported board type: ${selectedBoard}`);
        }
    } catch (error) {
        console.error('Error during compile or flash:', error);

        let errorMessage = error.message || 'Build or upload failed';
        // If the server returned an error Blob, parse its JSON text
        if (error.response && error.response.data) {
            if (error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    errorMessage = json.error || json.message || text;
                } catch (_) {
                    try {
                        errorMessage = await error.response.data.text();
                    } catch (__) { }
                }
            } else if (typeof error.response.data === 'object') {
                errorMessage = error.response.data.error || error.response.data.message || JSON.stringify(error.response.data);
            }
        }

        progressModal.setMode('error');
        progressModal.setErrorMsg(errorMessage);
        progressModal.appendLog(`\n[Process Error]: ${errorMessage}\n`);
        throw error;
    } finally {
        resetAllBlocks(arduinoBlocks, armBlocks, carBlocks, spiderBlocks, sensorBlock, actuatorBlock, displayBlock, stemEngine, pythonImports, pythonRoboCar, pythonRoboArm, pythonRoboSpider, esp32Blocks, stemSensorBlock, stemActuatorBlock);
        codeGeneration.clearLoopCode();
    }
}

export default generateAndUploadCode;
